import { InputBase } from "components/Input"
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  Group,
  Popover,
  FieldError,
} from "react-aria-components"
import {
  CalendarDay,
  CaretDown,
  ChevronLeft,
  ChevronRight,
  Times,
} from "components/Icon"
import {
  CalendarDateTime,
  getLocalTimeZone,
  toCalendarDateTime,
  today,
} from "@internationalized/date"
import { useTranslator } from "components/Translator"
import { useMemo, useState } from "react"
import classNames from "classnames"

/**
 * @typedef {CalendarDateTime} DateValue
 */

/**
 * @param {object} props
 * @param {DateValue} [props.initialValue]
 * @param {DateValue} [props.value]
 * @param {(date: DateValue) => void} props.onChange
 * @param {DateValue} [props.minDate]
 * @param {DateValue} [props.maxDate]
 * @param {string} props.name
 * @param {string} props.id
 * @param {"date" | "datetime"} [props.variant="date"]
 * @param {string} [props."aria-labelledby"]
 * @param {string} [props."aria-label"]
 */
export function InputDate({
  initialValue,
  value,
  onChange,
  minDate,
  maxDate,
  name,
  id,
  variant = "date",
  "aria-labelledby": ariaLabelledBy,
  "aria-label": ariaLabel,
}) {
  const startYear = minDate?.year ?? 1900
  const currentFullYear = today(getLocalTimeZone()).year
  const endYear = maxDate?.year ?? currentFullYear + 10

  const years = useMemo(
    () =>
      Array.from({ length: endYear - startYear + 1 }, (_, index) => {
        const year = startYear + index

        return { value: year, label: String(year) }
      }),
    [endYear, startYear],
  )

  const translator = useTranslator()

  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => {
        const date = new CalendarDateTime(currentFullYear, index + 1, 1)

        return {
          value: index + 1,
          label: date
            .toDate(getLocalTimeZone())
            .toLocaleDateString(translator.locale, { month: "long" }),
        }
      }),
    [currentFullYear, translator.locale],
  )

  const [visibleDate, setVisibleDate] = useState(() => {
    const todayDate = toCalendarDateTime(today(getLocalTimeZone()))

    return initialValue ?? value ?? todayDate
  })

  function handleMonthChange(event) {
    const newMonth = parseInt(event.target.value)

    setVisibleDate(
      new CalendarDateTime(visibleDate.year, newMonth, visibleDate.day),
    )
  }

  function handleYearChange(event) {
    const newYear = parseInt(event.target.value)

    setVisibleDate(
      new CalendarDateTime(newYear, visibleDate.month, visibleDate.day),
    )
  }

  /**
   * @param {CalendarDateTime} value
   */
  function handleChange(value) {
    const v =
      variant === "date"
        ? value.set({ hour: 12, minute: 0, seconds: 0, millisecond: 0 })
        : value

    onChange?.(v)
  }

  return (
    <>
      <DatePicker
        className={"w-full text-left"}
        defaultValue={initialValue}
        value={value === undefined ? undefined : value}
        onChange={handleChange}
        granularity={"second"}
        minValue={minDate}
        maxValue={maxDate}
        id={id}
        aria-labelledby={ariaLabelledBy}
        aria-label={ariaLabel}
      >
        <InputBase startAdornment={<CalendarDay className={"w-4"} />}>
          <Group className={"flex w-full"}>
            <DateInput className={"grow leading-none"}>
              {(segment) => {
                if (
                  segment.type === "year" ||
                  segment.type === "month" ||
                  segment.type === "day" ||
                  (segment.type === "literal" && segment.text === "/")
                )
                  return <div className={"inline-block"}>{segment.text}</div>

                return (
                  <DateSegment
                    segment={segment}
                    className={classNames("relative z-10", {
                      "ml-2": segment.type === "hour",
                      hidden: variant === "date",
                    })}
                  />
                )
              }}
            </DateInput>

            <Button
              className={"before:content-[''] before:absolute before:inset-0"}
            >
              <CaretDown className={"w-3"} />
            </Button>

            <button
              type={"button"}
              className={classNames(
                "absolute right-8 top-1/2 -translate-y-1/2 p-2 z-10 outline-0",
                { hidden: !value },
              )}
              onClick={() => {
                onChange?.(null)
              }}
            >
              <Times className={"w-3"} />
            </button>
          </Group>

          <Popover className={"bg-white rounded-sm p-2 shadow-1"} offset={16}>
            <Dialog>
              <Calendar
                focusedValue={visibleDate}
                onFocusChange={setVisibleDate}
                className={"grid gap-5"}
              >
                <header
                  className={
                    "bg-grey-lighter rounded-sm flex items-stretch gap-x-2 overflow-clip h-14"
                  }
                >
                  <Button
                    slot={"previous"}
                    className={
                      "flex items-center justify-center aspect-square enabled:hover:bg-grey-light enabled:focus-visible:bg-grey-light enabled:active:bg-grey-medium disabled:opacity-30"
                    }
                  >
                    <ChevronLeft className={"w-2"} />
                  </Button>

                  <div
                    className={"grow flex items-center justify-between gap-4"}
                  >
                    <UnstyledSelect
                      value={visibleDate.month}
                      onChange={handleMonthChange}
                    >
                      {months.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </UnstyledSelect>

                    <UnstyledSelect
                      value={visibleDate.year}
                      onChange={handleYearChange}
                    >
                      {years.map((year) => (
                        <option key={year.value} value={year.value}>
                          {year.label}
                        </option>
                      ))}
                    </UnstyledSelect>
                  </div>

                  <Button
                    slot={"next"}
                    className={
                      "flex items-center justify-center aspect-square enabled:hover:bg-grey-light enabled:focus-visible:bg-grey-light enabled:active:bg-grey-medium disabled:opacity-30"
                    }
                  >
                    <ChevronRight className={"w-2"} />
                  </Button>
                </header>

                <CalendarGrid className={"w-full"} weekdayStyle={"short"}>
                  <CalendarGridHeader>
                    {(day) => (
                      <CalendarHeaderCell
                        className={"capitalize text-black/50 pb-2 font-normal"}
                      >
                        {day}
                      </CalendarHeaderCell>
                    )}
                  </CalendarGridHeader>

                  <CalendarGridBody>
                    {(date) => (
                      <CalendarCell
                        date={date}
                        className={
                          "text-center w-10 h-10 rounded-full flex items-center justify-center data-[outside-month=true]:hidden hover:bg-grey-lighter focus:bg-grey-lighter aria-disabled:hover:bg-transparent data-[selected=true]:bg-secondary-lighter data-[selected=true]:focus:bg-secondary-light data-selected:hover:bg-secondary-light aria-disabled:opacity-30 aria-disabled:cursor-default"
                        }
                      />
                    )}
                  </CalendarGridBody>
                </CalendarGrid>
              </Calendar>
            </Dialog>
          </Popover>
        </InputBase>

        <FieldError className={"text-error-default"} />
      </DatePicker>
      <input type={"hidden"} value={value?.toString() ?? ""} name={name} />
    </>
  )
}

function UnstyledSelect({ children, value, onChange }) {
  return (
    <div className={"relative"}>
      <select
        className={"appearance-none pr-4"}
        value={value}
        onChange={onChange}
      >
        {children}
      </select>

      <CaretDown
        className={
          "w-2 absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none"
        }
      />
    </div>
  )
}
